<template>
  <div class="products-selector">
    <base-input>
      <el-select
        v-model="productModel"
        style="width: calc(100% - 50px)"
        :placeholder="$t('COMMON.PRODUCT')"
        :filterable="filterable"
        :multiple="multiple"
        :disabled="disabled"
        remote
        :remote-method="getProducts"
        :collapse-tags="collapseTags"
        :loading="loading"
        aria-describedby="button-addon"
        @change="productChanged"
      >
        <el-option v-if="showAll" :value="null" :label="$t('COMMON.PRODUCT')">
        </el-option>
        <el-option v-if="allowNone" :value="null" :label="$t('COMMON.NONE')">
        </el-option>
        <el-option
          v-for="product in productList"
          :key="product.id"
          :value="product.id"
          :label="`${product.name}`"
        >
        </el-option>
      </el-select>
      <i
        style="cursor: pointer"
        slot="append"
        class="fas fa-search px-1"
        @click="openAdvancedSelector"
      ></i>
    </base-input>
    <products-selector-advanced
      ref="advancedSelector"
      class="d-none"
      :disabled="disabled"
      :includes="includes"
      :products="selectedProductsData"
      :filterReseller="filterReseller"
      :filterOrganization="filterOrganization"
      :filterWarehouse="filterWarehouse"
      :filterSupplier="filterSupplier"
      :filterProductsNotInWarehouseId="filterProductsNotInWarehouseId"
      :filterIds="filterIds"
      :filterIdsNotIn="filterIdsNotIn"
      :filterCanBeSold="filterCanBeSold"
      :filterCanBePurchased="filterCanBePurchased"
      @productChanged="handleAdvancedSelection"
    >
      <template #trigger="{ toggle }">
        <span @click="toggle"></span>
      </template>
    </products-selector-advanced>
  </div>
</template>

<script>
import { Option, Select } from "element-ui";
import { difference } from "lodash";
import { STATUS_ACTIVE } from "@/constants/products";
import ProductsSelectorAdvanced from "./ProductsSelectorAdvanced";

export default {
  name: "products-selector",

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    ProductsSelectorAdvanced,
  },

  computed: {
    selectedProductsData() {
      return this.findProductsByIds(this.productModel);
    },
  },

  props: {
    disabled: {
      type: Boolean,
      default: false,
      description: "Disable the input",
    },
    showAll: {
      type: Boolean,
      default: true,
      description: "Show the option all",
    },
    allowNone: {
      type: Boolean,
      default: false,
      description: "Show the option none",
    },
    filterable: {
      type: Boolean,
      default: true,
      description: "Can filter",
    },
    multiple: {
      type: Boolean,
      default: true,
      description: "Allow selecting multiple products",
    },
    product: {
      type: String,
      default: null,
      description: "Product id",
    },
    includes: {
      type: String,
      default: null,
      description: "Includes",
    },
    products: {
      type: Array,
      default: () => [],
      description: "Default Products",
    },
    filterReseller: {
      type: String,
      default: null,
      description: "Reseller id",
    },
    filterOrganization: {
      type: String,
      default: null,
      description: "Organization id",
    },
    filterWarehouse: {
      type: String,
      default: null,
      description: "Warehouse id",
    },
    filterSupplier: {
      type: String,
      default: null,
      description: "Supplier id",
    },
    filterProductsNotInWarehouseId: {
      type: [String, Number],
      default: null,
      description: "Warehouse id to exclude already linked products",
    },
    filterIds: {
      type: Array,
      default: null,
      description: "products ids",
    },
    filterIdsNotIn: {
      type: Array,
      default: () => [],
      description: "Id not in",
    },
    filterCanBeSold: {
      type: [Boolean, null],
      default: null,
      description: "Can the product be sold",
    },
    filterCanBePurchased: {
      type: [Boolean, null],
      default: null,
      description: "Can the product be purchased",
    },
    collapseTags: {
      type: Boolean,
      default: true,
      description: "Collapse tags in multiselect",
    },
  },

  data() {
    return {
      productModel: this.products.length
        ? [...this.products].map((item) => item.id)
        : [],
      productList: [],
      fetchedProducts: [],
      loading: false,
      product_status: STATUS_ACTIVE,
    };
  },

  created() {
    this.getProducts(null, this.products?.length ? this.products : null);
  },

  methods: {
    async getProducts(query = null, products = null) {
      try {
        this.loading = true;
        let params = {
          sort: "-created_at",
          filter: {
            ...(query ? { search: encodeURIComponent(query) } : {}),
            ...(products ? { ids: products.map((item) => item.id) } : {}),
            ...(this.product_status ? { status: this.product_status } : {}),
          },
          page: {
            number: 1,
            size: 50,
          },
          include: this.includes ? this.includes : undefined,
        };

        if (this.filterOrganization) {
          params = {
            ...params,
            filter: { ...params.filter, organization: this.filterOrganization },
          };
        }
        if (this.filterSupplier) {
          params = {
            ...params,
            filter: { ...params.filter, supplier: this.filterSupplier },
          };
        }
        if (this.filterIds) {
          params = {
            ...params,
            filter: { ...params.filter, ids: this.filterIds },
          };
        }
        if (this.filterProductsNotInWarehouseId) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              productsNotInWarehouse: this.filterProductsNotInWarehouseId,
            },
          };
        }
        if (this.filterIdsNotIn.length > 0) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              idsNotIn: this.filterIdsNotIn,
            },
          };
        }
        if (this.filterSupplier) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              supplierProduct: this.filterSupplier,
            },
          };
        }
        if (this.filterCanBeSold != null) {
          params = {
            ...params,
            filter: { ...params.filter, can_be_sold: this.filterCanBeSold },
          };
        }
        if (this.filterCanBePurchased != null) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              can_be_purchased: this.filterCanBePurchased,
            },
          };
        }

        await this.$store.dispatch("products/list", params);
        const list = (await this.$store.getters["products/list"]) || [];
        const defaultProducts = products || this.products;

        this.fetchedProducts = this.mergeProducts(this.fetchedProducts, list);
        if (defaultProducts?.length) {
          this.fetchedProducts = this.mergeProducts(
            this.fetchedProducts,
            defaultProducts
          );
        }

        let finalList = [...list];
        if (defaultProducts?.length) {
          finalList = this.mergeProducts(finalList, defaultProducts);
        }
        finalList = this.mergeProducts(
          finalList,
          this.findProductsByIds(this.productModel)
        );
        this.productList = finalList;
        this.ensureSelectedProductsInList();
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.loading = false;
      }
    },

    mergeProducts(primary = [], secondary = []) {
      const map = new Map();
      const result = [];
      const add = (item) => {
        if (item?.id && !map.has(item.id)) {
          map.set(item.id, item);
          result.push(item);
        }
      };
      primary?.forEach(add);
      secondary?.forEach(add);
      return result;
    },

    findProductsByIds(ids) {
      if (!ids?.length) {
        return [];
      }
      const sources = [
        this.productList || [],
        this.fetchedProducts || [],
        this.products || [],
      ];
      const map = new Map();
      sources.forEach((list) => {
        list?.forEach((item) => {
          if (item?.id && !map.has(item.id)) {
            map.set(item.id, item);
          }
        });
      });
      return ids.map((id) => map.get(id)).filter((item) => !!item);
    },

    ensureSelectedProductsInList() {
      if (!this.productModel?.length) {
        return;
      }
      const selectedProducts = this.findProductsByIds(this.productModel);
      if (selectedProducts.length) {
        this.productList = this.mergeProducts(
          this.productList,
          selectedProducts
        );
      }
    },

    productChanged(products) {
      const selectedProducts = this.findProductsByIds(products);
      this.emitSelection(products, selectedProducts);
    },

    handleAdvancedSelection(_relationships, selectedProducts) {
      const ids = selectedProducts.map((product) => product.id);
      this.emitSelection(ids, selectedProducts);
    },

    emitSelection(productsIds, selectedProducts = null) {
      this.productModel = productsIds;
      const resolvedProducts =
        selectedProducts && selectedProducts.length
          ? selectedProducts
          : this.findProductsByIds(productsIds);
      this.productList = this.mergeProducts(this.productList, resolvedProducts);
      this.$emit(
        "productChanged",
        [...productsIds].map((item) => ({ type: "products", id: item })),
        resolvedProducts
      );
    },

    openAdvancedSelector() {
      if (this.disabled) {
        return;
      }
      this.$refs.advancedSelector?.toggleModal?.();
    },

    closeAdvancedSelector() {
      this.$refs.advancedSelector?.close?.();
    },
  },

  watch: {
    products(products) {
      if (products?.length) {
        const productIds = products.map((item) => item.id);
        const hasDiff =
          difference(productIds, this.productModel).length !== 0 ||
          difference(this.productModel, productIds).length !== 0;
        if (hasDiff) {
          this.productModel = productIds;
          this.getProducts(null, products);
        }
      } else {
        this.productModel = [];
      }
    },
    filterOrganization() {
      this.getProducts();
    },
    filterReseller() {
      this.getProducts();
    },
    filterSupplier() {
      this.getProducts();
    },
    filterWarehouse() {
      this.getProducts();
    },
    filterIds() {
      this.getProducts();
    },
    filterIdsNotIn() {
      this.getProducts();
    },
    filterProductsNotInWarehouseId() {
      this.getProducts();
    },
    filterCanBeSold() {
      this.getProducts();
    },
    filterCanBePurchased() {
      this.getProducts();
    },
  },
};
</script>
<style scoped>
.products-selector >>> .el-select .el-tag {
  max-width: 100%;
  display: inline-flex;
  align-items: center;
}
.products-selector >>> .el-select .el-tag .el-select__tags-text {
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}
</style>
